import { createContext, ReactNode, useContext } from "react";

import { AdpFormModeType } from "../../types/formModeType";
import { ClientFileType } from "../../types/clientFileType";
import { isAdmin, isAuthorizer, isMotionSupport, isSalesRep } from "../../utils/user";
import { useStoreContext } from "../StoreProvider";
import { isPostSigningStatus } from "../../utils/status";

const ClientFileContext = createContext(
  {} as { clientFile: ClientFileType; formMode: AdpFormModeType }
);

export const useClientFileContext = () => {
  const context = useContext(ClientFileContext);
  if (context === undefined) {
    throw new Error(
      "useClientFileContext must be used within a ClientFileProvider"
    );
  }
  return context;
};

const ClientFileProvider = ({
  children,
  clientFile,
}: {
  children: ReactNode | ReactNode[];
  clientFile: ClientFileType;
}) => {
  const {
    state: { user },
  } = useStoreContext();
  const {
    status,
    ds_envelope_id,
    signing_authorizer,
    client: { authorizers, sales_reps },
  } = clientFile;

  // Basic permission check (ToDo: this should be redundant once API is hardened)
  if (
    (isAuthorizer(user) && !authorizers?.some((auth) => auth.id === user?.id)) ||
    (isSalesRep(user) && !sales_reps?.some((rep) => rep.id === user?.id))
  ) {
    throw new Error("You don't have access to this client");
  }

  // Determine what action should be taken on the file by the current user
  const envelopeExists = !!ds_envelope_id;
  const completedButCancelled = !!((clientFile.ds_signed_form?.name || clientFile.ds_signed_form_original?.name) && status === "Cancelled");

  const canEdit = (["NotStarted", "Progress", "Review", "Action"].includes(status) && (isAuthorizer(user) || isAdmin(user)));
  const canReview = (["Progress", "Review", "Action"].includes(status) && isSalesRep(user));
  const canInitiateSigning = (status === "PendingSignature" && !envelopeExists && (isAuthorizer(user) && (user?.id === signing_authorizer?.id || !signing_authorizer) || isMotionSupport(user) || isAdmin(user)));
  const isWaitingSigningStep = (status === "PendingSignature" && envelopeExists);
  const canShowStatus = isPostSigningStatus(status) || completedButCancelled;

  // Determine what mode to view the form in
  let formMode: AdpFormModeType = "view";
  if (canEdit) formMode = "edit";
  else if (canReview) formMode = "review";
  else if (canInitiateSigning) formMode = "initiateSigning";
  else if (isWaitingSigningStep) formMode = "waitingSigning";
  else if (canShowStatus) formMode = "status";
  else if (status === "Cancelled") formMode = "cancelled";
  else formMode = "view";

  if ((canEdit || canReview) && clientFile.locked_by && clientFile.locked_by.id !== user?.id) {
    formMode = "locked";
  }

  return (
    <ClientFileContext.Provider value={{ clientFile, formMode }}>
      {children}
    </ClientFileContext.Provider>
  );
};

export default ClientFileProvider;
